<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import { onBeforeMount } from 'vue'
import { notify } from '@kyvg/vue3-notification'

import { useAuthStore } from '@/stores/auth-store'
import { LicenseType } from '@/types/company'
import { DamageReportPreferences } from '@/types/damage-report'
import useForm from '@/hooks/use-form'

import MyRadioButton from '@/components/my-components/form/MyRadioButton.vue'
import MyRadioButtonGroup from '@/components/my-components/form/MyRadioButtonGroup.vue'
import MyCheckbox from '@/components/my-components/form/MyCheckbox.vue'
import MyForm from '@/components/my-components/form/MyForm.vue'
import MyButton from '@/components/my-components/MyButton.vue'

const authStore = useAuthStore()
const { t } = useI18n()

const form = useForm<DamageReportPreferences>({
    sendReportsToOwners: false,
    requireCustomerCompany: false,
    tourSelectable: false,
    createVehiclesInApp: true,
    createUnitsInApp: true,
    requireIncidentNote: false,
})

async function savePreferences() {
    const response = await form.submit(
        'PUT',
        window.route('dmr.company.preferences.update', { company: authStore.companyId }),
    )

    if (response) {
        authStore.licenses[LicenseType.DamageReportModule] = {
            ...authStore.licenses[LicenseType.DamageReportModule],
            ...form.data,
        }

        notify({
            title: t('licensePreferencesUpdated'),
            type: 'success',
        })
    }
}

onBeforeMount(() => {
    const prefs: Partial<DamageReportPreferences> =
        authStore.licenses[LicenseType.DamageReportModule] ?? {}

    form.reset({ ...prefs })
})
</script>

<template>
    <MyForm class="space-y-2" :errors="form.errors.value" @submit.prevent="savePreferences">
        <div
            v-if="authStore.hasLicense(LicenseType.TransportObjectOwnerCompany)"
            class="flex items-center space-x-2"
        >
            <MyCheckbox v-model="form.data.sendReportsToOwners" :label="t('sendReportsToOwner')" />
            <mdi:information v-tooltip="t('sendReportsToOwnerExplanation')" />
        </div>

        <div
            v-if="authStore.hasLicense(LicenseType.FjordlineTrailerInformation)"
            class="flex items-center space-x-2"
        >
            <MyCheckbox
                v-model="form.data.requireCustomerCompany"
                :label="t('requireCustomerCompany')"
            />
            <mdi:information v-tooltip="t('requireCustomerCompanyExplanation')" />
        </div>

        <div
            v-if="authStore.hasLicense(LicenseType.DeliveryManagementModule)"
            class="flex items-center space-x-2"
        >
            <MyCheckbox
                v-model="form.data.tourSelectable"
                :label="t('selectTourForDamageReport')"
            />
            <mdi:information v-tooltip="t('selectTourExplanation')" />
        </div>

        <MyRadioButtonGroup
            v-model="form.data.createVehiclesInApp"
            class="relative"
            :label="t('allowVehicleCreationInApp')"
        >
            <MyRadioButton :label="t('enabled')" :value="true" />
            <MyRadioButton :label="t('disabled')" :value="false" />

            <template #label>
                {{ t('allowVehicleCreationInApp') }}
                <mdi:information
                    v-tooltip="t('allowVehicleCreationInAppExplanation')"
                    class="absolute top-1 right-0"
                />
            </template>
        </MyRadioButtonGroup>

        <MyRadioButtonGroup
            v-model="form.data.createUnitsInApp"
            class="relative"
            :label="t('allowUnitCreationInApp')"
        >
            <MyRadioButton :label="t('enabled')" :value="true" />
            <MyRadioButton :label="t('disabled')" :value="false" />

            <template #label>
                {{ t('allowUnitCreationInApp') }}
                <mdi:information
                    v-tooltip="t('allowUnitCreationInAppExplanation')"
                    class="absolute top-1 right-0"
                />
            </template>
        </MyRadioButtonGroup>
        <MyRadioButtonGroup
            v-model="form.data.requireIncidentNote"
            class="relative"
            :label="t('requireIncidentNote')"
        >
            <MyRadioButton :label="t('enabled')" :value="true" />
            <MyRadioButton :label="t('disabled')" :value="false" />

            <template #label>
                {{ t('requireIncidentNote') }}
                <mdi:information
                    v-tooltip="t('requireIncidentNoteExplanation')"
                    class="absolute top-1 right-0"
                />
            </template>
        </MyRadioButtonGroup>
        <div class="flex justify-end pt-4">
            <MyButton
                type="submit"
                scheme="primary"
                :loading="form.loading.value"
                :disabled="form.loading.value"
            >
                {{ t('save') }}
            </MyButton>
        </div>
    </MyForm>
</template>
